/**
 * NetworkPrinterFields — Campos de conexión de red (host, puerto, relay) de la
 * impresora térmica principal.
 *
 * QUÉ EXPORTA: NetworkPrinterFields (named).
 */

import { Wifi, Hash, Network, Video } from "lucide-react";
import type { PosPrinterConfig } from "@/lib/native/posPrinterConfig";
import { POS_DEFAULT_NETWORK_HOST } from "@/lib/native/posPrinterConfig";
import {
  POS_HARDWARE_SECTION_LABEL,
  POS_WEB_PRINT_GUIDE_URL,
} from "../utils/userSettingsPosHardware.helpers";

interface NetworkPrinterFieldsProps {
  cfg: PosPrinterConfig;
  onChange: (patch: Partial<PosPrinterConfig>) => void;
  isWeb: boolean;
}

export function NetworkPrinterFields({
  cfg,
  onChange,
  isWeb,
}: NetworkPrinterFieldsProps) {
  return (
    <div className="space-y-3">
      <p className={POS_HARDWARE_SECTION_LABEL}>Dirección de red</p>
      <div className="relative">
        <Wifi size={16} className="abs-center-y left-3 text-gray-400" />
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={POS_DEFAULT_NETWORK_HOST}
          value={cfg.networkHost}
          onChange={(e) => onChange({ networkHost: e.target.value.trim() })}
          className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-semibold"
        />
      </div>
      <p className="px-0.5 text-[10px] text-gray-500 dark:text-slate-400 leading-snug">
        Por defecto usamos <strong>{POS_DEFAULT_NETWORK_HOST}</strong> para que
        pruebes rápido. Cada impresora puede tener otra IP: si falla, busca la
        correcta como indica el aviso de arriba.
      </p>
      <div className="relative">
        <Hash size={16} className="abs-center-y left-3 text-gray-400" />
        <input
          type="number"
          inputMode="numeric"
          placeholder="9100"
          value={cfg.networkPort || ""}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            onChange({
              networkPort: Number.isFinite(v)
                ? Math.min(65535, Math.max(1, v))
                : 9100,
            });
          }}
          className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-semibold"
        />
      </div>
      {/* Relay local: único puente del navegador web hacia el TCP de la impresora */}
      <div className="relative">
        <Network size={16} className="abs-center-y left-3 text-gray-400" />
        <input
          type="text"
          inputMode="url"
          autoComplete="off"
          placeholder="http://192.168.1.50:8765"
          value={cfg.networkRelayUrl}
          onChange={(e) => onChange({ networkRelayUrl: e.target.value.trim() })}
          className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-mono"
        />
      </div>
      <p className="px-0.5 text-[10px] text-gray-500 dark:text-slate-400 leading-snug">
        {isWeb ? (
          <>
            <strong>Relay local (obligatorio en navegador web):</strong> proceso
            Node/ESP32 en la misma red que recibe{" "}
            <code className="font-mono">POST /print?host=&amp;port=</code> con
            los bytes ESC/POS y los envía a la impresora por TCP. En APK y
            escritorio no se usa.
          </>
        ) : (
          "Solo se usa desde el navegador web (relay local). En esta app no es necesario."
        )}
      </p>
      {isWeb && (
        <a
          href={POS_WEB_PRINT_GUIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-0.5 text-[10px] font-bold text-primary-600 dark:text-primary-400 underline underline-offset-2"
        >
          <Video size={11} />
          Ver guía con video para configurar el relay
        </a>
      )}
    </div>
  );
}
